import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { useCompanyContext } from '../../hooks/useCompanyContext';

// Define the Role interface
export interface Role {
  id: string;
  name: string;
  description?: string;
  company?: string;
  is_system_role?: boolean;
  role_type?: string;
  user_count?: number;
  created_at?: string;
  updated_at?: string;
  permission_ids?: string[];
  category_ids?: string[];
  location_ids?: string[];
  status_ids?: string[];
  department_ids?: string[];
}

// Pagination interface for roles
export interface RolePaginatedResponse {
  count: number;
  limit: number;
  next: string | null;
  previous: string | null;
  results: Role[];
}

// Define the WorkflowRoleAssignment interface
export interface WorkflowRoleAssignment {
  id: string;
  workflow_template: string;
  role: string;
  role_name?: string;
  description?: string;
  created_at: string;
  updated_at: string;
  updated_by?: string;
  created_by?: string;
}

export interface WorkflowRoleAssignmentPaginated {
  count: number;
  next: string | null;
  previous: string | null;
  results: WorkflowRoleAssignment[];
}

// Define the Permission interface
export interface Permission {
  id: string;
  name: string;
  description?: string;
  action: string;
  entity_type: string;
  permission_details?: Permission;
}

// Define the Permissions paginated response interface
export interface PermissionsPaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Permission[];
}

// Define the RolePermission interface
export interface RolePermission {
  id: string;
  role: string;
  permission: string;
  permission_details: {
    id: string;
    name: string;
    description: string;
    action: string;
    entity_type: string;
  };
}

// Define the RolePermissionResponse interface
export interface RolePermissionResponse {
  role_id: string;
  permissions: Permission[];
}

// Define the CategoryAccess interface
export interface CategoryAccess {
  id: string;
  name: string;
  parent_id: string | null;
  has_access?: boolean;
}

// Define the RoleCategoryResponse interface
export interface RoleCategoryResponse {
  role_id: string;
  assigned_categories: CategoryAccess[];
}

// Define the LocationAccess interface
export interface LocationAccess {
  id: string;
  name: string;
  site_id?: string;
  parent_id: string | null;
  has_access?: boolean;
}

// Define the RoleLocationResponse interface
export interface RoleLocationResponse {
  role_id: string;
  assigned_locations: LocationAccess[];
}

// Define the StatusVisibility interface
export interface StatusVisibility {
  id: string;
  name: string;
  description?: string;
  is_visible?: boolean;
}

// Define the RoleStatusResponse interface
export interface RoleStatusResponse {
  role_id: string;
  visible_statuses: StatusVisibility[];
}

// Define the DepartmentAccess interface
export interface DepartmentAccess {
  id: string;
  role: string;
  department: string;
  department_name: string;
}

// Define the RoleDepartmentResponse interface
export interface RoleDepartmentResponse {
  role_id: string;
  assigned_departments: DepartmentAccess[];
}

// Define the RoleUser interface
export interface RoleUser {
  id: string;
  name: string;
  email: string;
  created_at: string;
}

// Define the RoleUserResponse interface
export interface RoleUserResponse {
  role_id: string;
  users: RoleUser[];
  total: number;
  page: number;
  limit: number;
}

// Define the RoleResponse interface for paginated role list
export interface RoleResponse {
  [x: string]: Role[];
  roles: Role[];
  // total: number;
  // page: number;
  // limit: number;
}

export interface PaginatedRoleResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Role[];
}

// Define the RoleDetailResponse interface
export interface RoleDetailResponse {
  company_details: any;
  role: Role;
  permissions: RolePermission[];
  locations: any[];
  categories: any[];
  statuses: any[];
  departments: DepartmentAccess[];
}

// Create API
export const roleApi = createApi({
  reducerPath: 'roleApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: [
    'Role',
    'RoleDetail',
    'WorkflowRole',
    'RolePermission',
    'RoleCategory',
    'RoleLocation',
    'RoleStatus',
    'RoleUser',
    'ManageRole', // New tag type for manage roles
  ],
  endpoints: (builder) => ({
    getRoles: builder.query<
      Role[],
      { search?: string; dropdown?: boolean } | void
    >({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        if (params && 'search' in params && params.search) {
          queryParams.append('search', params.search);
        }
        if (params && 'dropdown' in params && params.dropdown) {
          queryParams.append('dropdown', 'true');
        }
        return { url: 'roles/', params: queryParams };
      },
      providesTags: ['Role'],
    }),

    // New dropdown-specific endpoint
    getRolesDropdown: builder.query<
      RolePaginatedResponse,
      { search?: string; page?: number; limit?: number; dropdown?: boolean; } | void
    >({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
          if (params && 'search' in params && params.search) {
            queryParams.append('search', params.search);
          }
          if (params && 'page' in params && params.page) {
            queryParams.append('page', params.page.toString());
          }
          if (params && 'limit' in params && params.limit) {
            queryParams.append('limit', params.limit.toString());
          }
          if (params && 'dropdown' in params && params.dropdown) {
            queryParams.append('dropdown', 'true');
          }
        return { url: 'users/roles/dropdown/', params: queryParams };
      },
      providesTags: ['Role'],
    }),

    // Get company roles
    getCompanyRoles: builder.query<
      PaginatedRoleResponse,
      {
        companyId: string;
        page?: number;
        limit?: number;
        search?: string;
        roleType?: string;
        dropdown?: boolean;
      }
    >({
      query: ({
        companyId,
        page = 1,
        limit = 10,
        search,
        roleType,
        dropdown,
      }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());
        if (search) queryParams.append('search', search);
        if (roleType) queryParams.append('role_type', roleType);
        if (dropdown !== undefined) {
          queryParams.append('dropdown', String(dropdown));
        }
        return {
          url: `users/roles/`,
          params: queryParams,
        };
      },
      // Transform the response to handle both formats
      transformResponse: (response: Role[] | RoleResponse) => {
        // If response is an array, transform it to match RoleResponse format
        if (Array.isArray(response)) {
          console.log('Transforming array response to RoleResponse format');
          return response;
        }
        // Otherwise, return the response as is (assuming it's already in RoleResponse format)
        return response;
      },
      providesTags: (result, error, { companyId }) => [
        { type: 'Role', id: companyId },
        'Role',
      ],
    }),

    // Get role by ID
    getRole: builder.query<Role, { companyId: string; roleId: string }>({
      query: ({ companyId, roleId }) =>
        `users/roles/companies/${companyId}/roles/${roleId}/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'Role', id: roleId },
      ],
    }),

    // Get role details with permissions, locations, categories, and statuses
    getRoleDetail: builder.query<
      RoleDetailResponse,
      { roleId: string }
    >({
      query: ({ roleId }) => `users/manage-roles/${roleId}/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'RoleDetail', id: roleId },
        { type: 'Role', id: roleId },
      ],
    }),

    // Create role
    createRole: builder.mutation<
      Role,
      { companyId: string; roleData: Partial<Role> }
    >({
      query: ({ companyId, roleData }) => ({
        url: `users/roles/companies/${companyId}/roles/`,
        method: 'POST',
        body: roleData,
      }),
      invalidatesTags: (result, error, { companyId }) => [
        { type: 'Role', id: companyId },
        'Role',
      ],
    }),

    // Update role
    updateRole: builder.mutation<
      Role,
      { roleId: string; roleData: Partial<Role> }
    >({
      query: ({ roleId, roleData }) => ({
        url: `users/manage-roles/${roleId}/`,
        method: 'PUT',
        body: roleData,
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'Role', id: roleId },
        'Role',
      ],
    }),

    // Delete role
    deleteRole: builder.mutation<void, { companyId: string; roleId: string }>({
      query: ({ companyId, roleId }) => ({
        url: `users/roles/companies/${companyId}/roles/${roleId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { companyId }) => [
        { type: 'Role', id: companyId },
        'Role',
      ],
    }),

    // Get all permissions - UPDATED TO HANDLE PAGINATED RESPONSE
    // In roleApi (src/store/api/roleApi.ts)
    getPermissions: builder.query<{results: Permission[];next: string | null;count: number;},
    {page?: number;limit?: number;search?: string;dropdown?: boolean;}>
    ({
      queryFn: async (arg, _api, _extraOptions, baseQuery) => {
        try {
          const page = arg?.page ?? 1;
          const limit = arg?.limit ?? 10;
          const search = arg?.search ?? '';
          const dropdown = arg?.dropdown;

          const response = await baseQuery({
            url: `users/permissions/?page=${page}&limit=${limit}${
              search ? `&search=${search}` : ''
            }${dropdown ? '&dropdown=true' : ''}`,
          });

          if (response.error) {
            return { error: response.error };
          }

          const data = response.data as any;

          // ✅ IMPORTANT: return full pagination object
          if (data && 'results' in data) {
            return {
              data: {
                results: data.results,
                next: data.next,
                count: data.count,
              },
            };
          }

          // fallback (if array)
          return {
            data: {
              results: Array.isArray(data) ? data : [],
              next: null,
              count: Array.isArray(data) ? data.length : 0,
            },
          };
        } catch (err: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              data: undefined,
              error: err?.message ?? 'Unknown error',
            },
          };
        }
      },
      providesTags: ['RolePermission'],
    }),
    
    // Get role permissions
    getRolePermissions: builder.query<
      RolePermissionResponse,
      { companyId: string; roleId: string }
    >({
      query: ({ companyId, roleId }) => `users/roles/${roleId}/permissions/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'RolePermission', id: roleId },
        'RolePermission',
      ],
    }),

    // Assign permissions to role
    assignPermissionsToRole: builder.mutation<
      RolePermissionResponse,
      {
        companyId: string;
        roleId: string;
        permissionIds: string[];
      }
    >({
      query: ({ companyId, roleId, permissionIds }) => ({
        url: `users/roles/companies/${companyId}/roles/${roleId}/permissions/`,
        method: 'POST',
        body: { permission_ids: permissionIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'RolePermission', id: roleId },
        'RolePermission',
      ],
    }),

    // Remove permission from role
    removePermissionFromRole: builder.mutation<
      void,
      {
        companyId: string;
        roleId: string;
        permissionId: string;
      }
    >({
      query: ({ companyId, roleId, permissionId }) => ({
        url: `users/roles/companies/${companyId}/roles/${roleId}/permissions/${permissionId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'RolePermission', id: roleId },
        'RolePermission',
      ],
    }),

    // Get role categories
    getRoleCategories: builder.query<
      RoleCategoryResponse,
      { companyId: string; roleId: string }
    >({
      query: ({ companyId, roleId }) =>
        `users/roles/companies/${companyId}/roles/${roleId}/categories/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'RoleCategory', id: roleId },
        'RoleCategory',
      ],
    }),

    // Assign categories to role
    assignCategoriesToRole: builder.mutation<
      RoleCategoryResponse,
      {
        companyId: string;
        roleId: string;
        categoryIds: string[];
      }
    >({
      query: ({ companyId, roleId, categoryIds }) => ({
        url: `users/roles/companies/${companyId}/roles/${roleId}/categories/`,
        method: 'POST',
        body: { category_ids: categoryIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'RoleCategory', id: roleId },
        'RoleCategory',
      ],
    }),

    // Remove category from role
    removeCategoryFromRole: builder.mutation<
      void,
      {
        companyId: string;
        roleId: string;
        categoryId: string;
      }
    >({
      query: ({ companyId, roleId, categoryId }) => ({
        url: `users/roles/companies/${companyId}/roles/${roleId}/categories/${categoryId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'RoleCategory', id: roleId },
        'RoleCategory',
      ],
    }),

    // Get role locations
    getRoleLocations: builder.query<
      RoleLocationResponse,
      { companyId: string; roleId: string }
    >({
      query: ({ companyId, roleId }) =>
        `users/roles/companies/${companyId}/roles/${roleId}/locations/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'RoleLocation', id: roleId },
        'RoleLocation',
      ],
    }),

    // Assign locations to role
    assignLocationsToRole: builder.mutation<
      RoleLocationResponse,
      {
        companyId: string;
        roleId: string;
        locationIds: string[];
      }
    >({
      query: ({ companyId, roleId, locationIds }) => ({
        url: `users/roles/companies/${companyId}/roles/${roleId}/locations/`,
        method: 'POST',
        body: { location_ids: locationIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'RoleLocation', id: roleId },
        'RoleLocation',
      ],
    }),

    // Remove location from role
    removeLocationFromRole: builder.mutation<
      void,
      {
        companyId: string;
        roleId: string;
        locationId: string;
      }
    >({
      query: ({ companyId, roleId, locationId }) => ({
        url: `users/roles/companies/${companyId}/roles/${roleId}/locations/${locationId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'RoleLocation', id: roleId },
        'RoleLocation',
      ],
    }),

    // Get role statuses
    getRoleStatuses: builder.query<
      RoleStatusResponse,
      { companyId: string; roleId: string }
    >({
      query: ({ companyId, roleId }) =>
        `users/roles/companies/${companyId}/roles/${roleId}/statuses/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'RoleStatus', id: roleId },
        'RoleStatus',
      ],
    }),

    // Assign statuses to role
    assignStatusesToRole: builder.mutation<
      RoleStatusResponse,
      {
        companyId: string;
        roleId: string;
        statusIds: string[];
      }
    >({
      query: ({ companyId, roleId, statusIds }) => ({
        url: `users/roles/companies/${companyId}/roles/${roleId}/statuses/`,
        method: 'POST',
        body: { status_ids: statusIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'RoleStatus', id: roleId },
        'RoleStatus',
      ],
    }),

    // Remove status from role
    removeStatusFromRole: builder.mutation<
      void,
      {
        companyId: string;
        roleId: string;
        statusId: string;
      }
    >({
      query: ({ companyId, roleId, statusId }) => ({
        url: `users/roles/companies/${companyId}/roles/${roleId}/statuses/${statusId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'RoleStatus', id: roleId },
        'RoleStatus',
      ],
    }),

    // Get role users
    getRoleUsers: builder.query<
      RoleUserResponse,
      {
        companyId: string;
        roleId: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ companyId, roleId, page = 1, limit = 10 }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());
        return {
          url: `users/roles/companies/${companyId}/roles/${roleId}/users/`,
          params: queryParams,
        };
      },
      providesTags: (result, error, { roleId }) => [
        { type: 'RoleUser', id: roleId },
        'RoleUser',
      ],
    }),

    // Get system roles
    getSystemRoles: builder.query<Role[], void>({
      query: () => 'system-roles/',
      providesTags: ['Role'],
    }),

    // Get workflow template roles
    getWorkflowTemplateRoles: builder.query<
      WorkflowRoleAssignmentPaginated,
      string
    >({
      query: (workflowTemplateId) =>
        `workflows/workflow-templates/${workflowTemplateId}/roles/`,
      providesTags: (result, error, workflowTemplateId) => [
        { type: 'WorkflowRole', id: workflowTemplateId },
        'WorkflowRole',
      ],
    }),

    // Assign role to workflow template
    assignRoleToWorkflowTemplate: builder.mutation<
      WorkflowRoleAssignment,
      { workflowTemplateId: string; roleId: string; description?: string }
    >({
      query: ({ workflowTemplateId, roleId, description }) => ({
        url: `workflows/workflow-templates/${workflowTemplateId}/roles/`,
        method: 'POST',
        body: { role: roleId, description },
      }),
      invalidatesTags: (result, error, { workflowTemplateId }) => [
        { type: 'WorkflowRole', id: workflowTemplateId },
        'WorkflowRole',
      ],
    }),

    // Remove role from workflow template
    removeRoleFromWorkflowTemplate: builder.mutation<
      void,
      { workflowTemplateId: string; roleId: string }
    >({
      query: ({ workflowTemplateId, roleId }) => ({
        url: `workflows/workflow-templates/${workflowTemplateId}/roles/${roleId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { workflowTemplateId }) => [
        { type: 'WorkflowRole', id: workflowTemplateId },
        'WorkflowRole',
      ],
    }),

    // ===== NEW ENDPOINTS FOR ROLE MANAGEMENT (users/manage-roles/) =====

    // Get all manage roles
    getManageRoles: builder.query<
      RolePaginatedResponse,
      Record<string, any>
    >({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        
        // Handle pagination
        queryParams.append('page', (params.page || 1).toString());
        queryParams.append('limit', (params.limit || 10).toString());
        
        // Handle all other parameters dynamically
        Object.entries(params).forEach(([key, value]) => {
          if (key !== 'page' && key !== 'limit' && value !== undefined && value !== null && value !== '') {
            queryParams.append(key, String(value));
          }
        });
        
        return {
          url: 'users/manage-roles/',
          params: queryParams,
        };
      },
      transformResponse: (response: RolePaginatedResponse) => {
        console.log('Manage Roles API Response:', response);
        return response;
      },
      providesTags: ['ManageRole'],
    }),

    // Get manage role by ID
    getManageRole: builder.query<Role, { roleId: string }>({
      query: ({ roleId }) => `users/manage-roles/${roleId}/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: roleId },
      ],
    }),

    // Create manage role
    createManageRole: builder.mutation<Role, { roleData: Partial<Role> }>({
      query: ({ roleData }) => ({
        url: 'users/manage-roles/',
        method: 'POST',
        body: roleData,
      }),
      invalidatesTags: ['ManageRole'],
    }),

    // Update manage role
    updateManageRole: builder.mutation<
      Role,
      { roleId: string; roleData: Partial<Role> }
    >({
      query: ({ roleId, roleData }) => ({
        url: `users/manage-roles/${roleId}/`,
        method: 'PUT',
        body: roleData,
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: roleId },
        'ManageRole',
        { type: 'RoleDetail', id: roleId },
        { type: 'Role', id: roleId },
      ],
    }),

    // Patch manage role
    patchManageRole: builder.mutation<
      Role,
      { roleId: string; roleData: Partial<Role> }
    >({
      query: ({ roleId, roleData }) => ({
        url: `users/manage-roles/${roleId}/`,
        method: 'PATCH',
        body: roleData,
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: roleId },
        'ManageRole',
      ],
    }),

    // Delete manage role
    deleteManageRole: builder.mutation<void, { roleId: string }>({
      query: ({ roleId }) => ({
        url: `users/manage-roles/${roleId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ManageRole'],
    }),

    // Get manage role permissions
    getManageRolePermissions: builder.query<
      RolePermissionResponse,
      { roleId: string }
    >({
      query: ({ roleId }) => `users/manage-roles/${roleId}/permissions/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-permissions` },
        'ManageRole',
      ],
    }),

    // Assign permissions to manage role
    assignPermissionsToManageRole: builder.mutation<
      RolePermissionResponse,
      {
        roleId: string;
        permissionIds: string[];
      }
    >({
      query: ({ roleId, permissionIds }) => ({
        url: `users/manage-roles/${roleId}/permissions/`,
        method: 'POST',
        body: { permission_ids: permissionIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-permissions` },
        'ManageRole',
      ],
    }),

    // Remove permission from manage role
    removePermissionFromManageRole: builder.mutation<
      void,
      {
        roleId: string;
        permissionId: string;
      }
    >({
      query: ({ roleId, permissionId }) => ({
        url: `users/manage-roles/${roleId}/permissions/${permissionId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-permissions` },
        'ManageRole',
      ],
    }),

    // Get manage role categories
    getManageRoleCategories: builder.query<
      RoleCategoryResponse,
      { roleId: string }
    >({
      query: ({ roleId }) => `users/manage-roles/${roleId}/categories/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-categories` },
        'ManageRole',
      ],
    }),

    // Assign categories to manage role
    assignCategoriesToManageRole: builder.mutation<
      RoleCategoryResponse,
      {
        roleId: string;
        categoryIds: string[];
      }
    >({
      query: ({ roleId, categoryIds }) => ({
        url: `users/manage-roles/${roleId}/categories/`,
        method: 'POST',
        body: { category_ids: categoryIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-categories` },
        'ManageRole',
      ],
    }),

    // Remove category from manage role
    removeCategoryFromManageRole: builder.mutation<
      void,
      {
        roleId: string;
        categoryId: string;
      }
    >({
      query: ({ roleId, categoryId }) => ({
        url: `users/manage-roles/${roleId}/categories/${categoryId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-categories` },
        'ManageRole',
      ],
    }),

    // Get manage role locations
    getManageRoleLocations: builder.query<
      RoleLocationResponse,
      { roleId: string }
    >({
      query: ({ roleId }) => `users/manage-roles/${roleId}/locations/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-locations` },
        'ManageRole',
      ],
    }),

    // Assign locations to manage role
    assignLocationsToManageRole: builder.mutation<
      RoleLocationResponse,
      {
        roleId: string;
        locationIds: string[];
      }
    >({
      query: ({ roleId, locationIds }) => ({
        url: `users/manage-roles/${roleId}/locations/`,
        method: 'POST',
        body: { location_ids: locationIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-locations` },
        'ManageRole',
      ],
    }),

    // Remove location from manage role
    removeLocationFromManageRole: builder.mutation<
      void,
      {
        roleId: string;
        locationId: string;
      }
    >({
      query: ({ roleId, locationId }) => ({
        url: `users/manage-roles/${roleId}/locations/${locationId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-locations` },
        'ManageRole',
      ],
    }),

    // Get manage role statuses
    getManageRoleStatuses: builder.query<
      RoleStatusResponse,
      { roleId: string }
    >({
      query: ({ roleId }) => `users/manage-roles/${roleId}/statuses/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-statuses` },
        'ManageRole',
      ],
    }),

    // Assign statuses to manage role
    assignStatusesToManageRole: builder.mutation<
      RoleStatusResponse,
      {
        roleId: string;
        statusIds: string[];
      }
    >({
      query: ({ roleId, statusIds }) => ({
        url: `users/manage-roles/${roleId}/statuses/`,
        method: 'POST',
        body: { status_ids: statusIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-statuses` },
        'ManageRole',
      ],
    }),

    // Remove status from manage role
    removeStatusFromManageRole: builder.mutation<
      void,
      {
        roleId: string;
        statusId: string;
      }
    >({
      query: ({ roleId, statusId }) => ({
        url: `users/manage-roles/${roleId}/statuses/${statusId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-statuses` },
        'ManageRole',
      ],
    }),

    // Get manage role departments
    getManageRoleDepartments: builder.query<
      RoleDepartmentResponse,
      { roleId: string }
    >({
      query: ({ roleId }) => `users/manage-roles/${roleId}/departments/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-departments` },
        'ManageRole',
      ],
    }),

    // Assign departments to manage role
    assignDepartmentsToManageRole: builder.mutation<
      RoleDepartmentResponse,
      {
        roleId: string;
        departmentIds: string[];
      }
    >({
      query: ({ roleId, departmentIds }) => ({
        url: `users/manage-roles/${roleId}/departments/`,
        method: 'POST',
        body: { department_ids: departmentIds },
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-departments` },
        'ManageRole',
      ],
    }),

    // Remove department from manage role
    removeDepartmentFromManageRole: builder.mutation<
      void,
      {
        roleId: string;
        departmentId: string;
      }
    >({
      query: ({ roleId, departmentId }) => ({
        url: `users/manage-roles/${roleId}/departments/${departmentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-departments` },
        'ManageRole',
      ],
    }),

    // Get manage role users
    getManageRoleUsers: builder.query<
      RoleUserResponse,
      {
        roleId: string;
        page?: number;
        limit?: number;
      }
    >({
      query: ({ roleId, page = 1, limit = 10 }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('page', page.toString());
        queryParams.append('limit', limit.toString());
        return {
          url: `users/manage-roles/${roleId}/users/`,
          params: queryParams,
        };
      },
      providesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-users` },
        'ManageRole',
      ],
    }),

    // Get manage role details (comprehensive)
    getManageRoleDetail: builder.query<RoleDetailResponse, { roleId: string }>({
      query: ({ roleId }) => `users/manage-roles/${roleId}/details/`,
      providesTags: (result, error, { roleId }) => [
        { type: 'ManageRole', id: `${roleId}-detail` },
        'ManageRole',
      ],
    }),
  }),
});

// Export all hooks (existing + new)
export const {
  // Existing hooks
  useGetRolesQuery,
  useGetRolesDropdownQuery,
  useGetCompanyRolesQuery,
  useGetRoleQuery,
  useGetRoleDetailQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetPermissionsQuery,
  useGetRolePermissionsQuery,
  useAssignPermissionsToRoleMutation,
  useRemovePermissionFromRoleMutation,
  useGetRoleCategoriesQuery,
  useAssignCategoriesToRoleMutation,
  useRemoveCategoryFromRoleMutation,
  useGetRoleLocationsQuery,
  useAssignLocationsToRoleMutation,
  useRemoveLocationFromRoleMutation,
  useGetRoleStatusesQuery,
  useAssignStatusesToRoleMutation,
  useRemoveStatusFromRoleMutation,
  useGetRoleUsersQuery,
  useGetSystemRolesQuery,
  useGetWorkflowTemplateRolesQuery,
  useAssignRoleToWorkflowTemplateMutation,
  useRemoveRoleFromWorkflowTemplateMutation,

  // New hooks for manage roles
  useGetManageRolesQuery,
  useGetManageRoleQuery,
  useCreateManageRoleMutation,
  useUpdateManageRoleMutation,
  usePatchManageRoleMutation,
  useDeleteManageRoleMutation,
  useGetManageRolePermissionsQuery,
  useAssignPermissionsToManageRoleMutation,
  useRemovePermissionFromManageRoleMutation,
  useGetManageRoleCategoriesQuery,
  useAssignCategoriesToManageRoleMutation,
  useRemoveCategoryFromManageRoleMutation,
  useGetManageRoleLocationsQuery,
  useAssignLocationsToManageRoleMutation,
  useRemoveLocationFromManageRoleMutation,
  useGetManageRoleStatusesQuery,
  useAssignStatusesToManageRoleMutation,
  useRemoveStatusFromManageRoleMutation,
  useGetManageRoleDepartmentsQuery,
  useAssignDepartmentsToManageRoleMutation,
  useRemoveDepartmentFromManageRoleMutation,
  useGetManageRoleUsersQuery,
  useGetManageRoleDetailQuery,
} = roleApi;

// Existing custom hook to get company roles with the current company context
export const useCompanyRoles = (
  params: {
    page?: number;
    limit?: number;
    search?: string;
    roleType?: string;
  } = {}
) => {
  const { companyId } = useCompanyContext();

  return useGetCompanyRolesQuery(
    { companyId: companyId || '', ...params },
    { skip: !companyId }
  );
};

// New custom hook for manage roles (no company context needed)
export const useManageRoles = (
  params: Record<string, any> = {}
) => {
  return useGetManageRolesQuery(params);
};

// Custom hook for manage role details
export const useManageRoleDetails = (roleId: string) => {
  return useGetManageRoleDetailQuery({ roleId }, { skip: !roleId });
};

// Custom hook for manage role permissions
export const useManageRolePermissions = (roleId: string) => {
  return useGetManageRolePermissionsQuery({ roleId }, { skip: !roleId });
};

// Custom hook for manage role users
export const useManageRoleUsers = (
  roleId: string,
  params: { page?: number; limit?: number } = {}
) => {
  return useGetManageRoleUsersQuery({ roleId, ...params }, { skip: !roleId });
};
